import { BudgetSummaryDTO } from '../../../../project/project.type';

const normalizeValue = (val: any): number => {
  if (val === null || val === undefined || val === '-' || val === 'null' || val === 'undefined' || isNaN(Number(val))) {
    return 0;
  }
  return Number(val) || 0;
};

/**
 * Recomputes a draw's budget summary from its line items, preserving the manually-entered fields
 * (lessPreviousFundings, adjustmentsByCDCC, requestedAmount, estRetainageReleasedThisPeriod) from
 * the stored summary. With no line items the stored summary is returned with its numbers coerced.
 *
 * Extracted so each draw's tab in Review Budget can run it independently.
 */
export const calculateBudgetSummaryFromLineItems = (
  lineItems: any[] | undefined,
  budgetSummary: BudgetSummaryDTO | undefined,
): BudgetSummaryDTO | undefined => {
  if (!lineItems || lineItems.length === 0) {
    if (!budgetSummary) return budgetSummary;
    return {
      ...budgetSummary,
      originalContractBudgetSum: normalizeValue(budgetSummary.originalContractBudgetSum),
      reallocationsChangeOrders: normalizeValue(budgetSummary.reallocationsChangeOrders),
      contractBudgetSumToDate: normalizeValue(budgetSummary.contractBudgetSumToDate),
      totalCompletedAndStored: normalizeValue(budgetSummary.totalCompletedAndStored),
      totalRetainageHeld: normalizeValue(budgetSummary.totalRetainageHeld),
      totalRetainageHeldPercentage: normalizeValue(budgetSummary.totalRetainageHeldPercentage),
      totalEarnedLessRetainage: normalizeValue(budgetSummary.totalEarnedLessRetainage),
      lessPreviousFundings: normalizeValue(budgetSummary.lessPreviousFundings),
      currentPaymentDue: normalizeValue(budgetSummary.currentPaymentDue),
      balanceToCompletePlusRetainage: normalizeValue(budgetSummary.balanceToCompletePlusRetainage),
      adjustmentsByCDCC: normalizeValue(budgetSummary.adjustmentsByCDCC),
      totalAmountReccForFunding: normalizeValue(budgetSummary.totalAmountReccForFunding),
      requestedAmount: normalizeValue(budgetSummary.requestedAmount),
      difference: normalizeValue(budgetSummary.difference),
      estRetainageReleasedThisPeriod: normalizeValue(budgetSummary.estRetainageReleasedThisPeriod),
      totalContractBudgetPerComplete: normalizeValue(budgetSummary.totalContractBudgetPerComplete),
    } as BudgetSummaryDTO;
  }

  const filteredLineItems = lineItems.filter((item: any) => !item.isDeleted);

  const originalContractBudgetSum = Number(
    filteredLineItems.reduce((sum: number, li: any) => sum + Number(Number(li.originalBudget || 0).toFixed(2)), 0).toFixed(2),
  );
  const reallocsChangeOrdersSum = Number(
    filteredLineItems
      .reduce(
        (sum: number, li: any) => sum + Number(Number(li.reallocation || 0).toFixed(2)) + Number(Number(li.changeOrder || 0).toFixed(2)),
        0,
      )
      .toFixed(2),
  );
  const contractBudgetSumToDate = Number((originalContractBudgetSum + reallocsChangeOrdersSum).toFixed(2));
  const totalCompletedAndStored = Number(
    filteredLineItems
      .reduce((sum: number, li: any) => {
        const cdccApproved =
          typeof li.cdccApproved === 'number' ? li.cdccApproved : Number(li.completeThisPeriod || 0) + Number(li.storedThisPeriod || 0);
        return sum + Number(Number(li.previousApplication || 0).toFixed(2)) + Number(Number(cdccApproved || 0).toFixed(2));
      }, 0)
      .toFixed(2),
  );
  const totalRetainageHeld = -Number(filteredLineItems.reduce((sum: number, li: any) => sum + Number(li.retainage || 0), 0).toFixed(2));
  const totalRetainageHeldPercentage = totalCompletedAndStored > 0 ? (-totalRetainageHeld / totalCompletedAndStored) * 100 : 0;
  const balanceToCompleteSum = Number((contractBudgetSumToDate - totalCompletedAndStored).toFixed(2));

  const totalEarnedLessRetainage = Number((totalCompletedAndStored + totalRetainageHeld).toFixed(2));
  const lessPrevFunding = Number(Number(budgetSummary?.lessPreviousFundings || 0).toFixed(2));
  const currentPaymentDue = Number((totalEarnedLessRetainage - lessPrevFunding).toFixed(2));
  const balanceToCompletePlusRetainage = Number((balanceToCompleteSum - totalRetainageHeld).toFixed(2));
  const adjustmentsByCDCC = Number(Number(budgetSummary?.adjustmentsByCDCC || 0).toFixed(2));
  const totalAmountReccForFunding = Number((adjustmentsByCDCC + currentPaymentDue).toFixed(2));
  const requestedAmount = Number(Number(budgetSummary?.requestedAmount || 0).toFixed(2));
  const difference = Number((totalAmountReccForFunding - requestedAmount).toFixed(2));
  const estRetainageReleasedThisPeriod = Number(Number(budgetSummary?.estRetainageReleasedThisPeriod || 0).toFixed(2));
  const totalContractBudgetPerComplete =
    contractBudgetSumToDate === 0 ? 0 : Number(((totalCompletedAndStored / contractBudgetSumToDate) * 100).toFixed(2));

  return {
    ...budgetSummary,
    originalContractBudgetSum: normalizeValue(originalContractBudgetSum),
    reallocationsChangeOrders: normalizeValue(reallocsChangeOrdersSum),
    contractBudgetSumToDate: normalizeValue(contractBudgetSumToDate),
    totalCompletedAndStored: normalizeValue(totalCompletedAndStored),
    totalRetainageHeld: normalizeValue(totalRetainageHeld),
    totalRetainageHeldPercentage: normalizeValue(totalRetainageHeldPercentage),
    totalEarnedLessRetainage: normalizeValue(totalEarnedLessRetainage),
    lessPreviousFundings: normalizeValue(lessPrevFunding),
    currentPaymentDue: normalizeValue(currentPaymentDue),
    balanceToCompletePlusRetainage: normalizeValue(balanceToCompletePlusRetainage),
    adjustmentsByCDCC: normalizeValue(adjustmentsByCDCC),
    totalAmountReccForFunding: normalizeValue(totalAmountReccForFunding),
    requestedAmount: normalizeValue(requestedAmount),
    difference: normalizeValue(difference),
    estRetainageReleasedThisPeriod: normalizeValue(estRetainageReleasedThisPeriod),
    totalContractBudgetPerComplete: normalizeValue(totalContractBudgetPerComplete),
  } as BudgetSummaryDTO;
};
